import React from "react";
import {
  Edit,
  MessageSquare,
  Rss,
  Twitter,
  Facebook,
  Instagram,
  Calendar,
  Link2,
  MapPin,
  Users,
  Heart,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { toggleFollowCreator, UserData } from "../../store/dreamworldSlice";

interface ProfileHeaderProps {
  user: UserData;
  isOwner: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ user, isOwner }) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );

  const handleFollow = () => {
    if (currentUser) {
      dispatch(
        toggleFollowCreator({
          currentUserId: currentUser.user.uid,
          creatorId: user.uid,
        })
      );
    }
  };

  const isFollowing = currentUser?.userData?.following?.includes(user.uid);

  return (
    <div className="relative overflow-hidden">
      {/* Cover Image with Gradient Overlay */}
      <div className="relative h-48 md:h-72">
        <img
          src={
            user.backImageUrl ||
            "https://plus.unsplash.com/premium_photo-1661874048310-23eb9c3b6693?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTd8fGNpbmVtYXRpY3xlbnwwfHwwfHx8MA%3D%3D"
          }
          alt="Cover"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent"></div>

        {/* Edit Profile Button - Top Right */}
        {isOwner && (
          <div className="absolute top-4 right-4 z-10">
            <Link to="/settings">
              <button className="bg-gray-900/80 backdrop-blur-md hover:bg-gray-800/90 text-white font-medium py-2.5 px-4 rounded-full flex items-center space-x-2 transition-all duration-200 transform hover:scale-105 border border-gray-700/50 shadow-lg">
                <Edit size={16} />
                <span>Edit Profile</span>
              </button>
            </Link>
          </div>
        )}
      </div>

      {/* Profile Content */}
      <div className="relative bg-gray-900/95 backdrop-blur-sm border-t border-gray-700/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:space-x-8">
            {/* Avatar */}
            <div className="relative -mt-16 lg:-mt-20 flex justify-center lg:justify-start">
              <div className="relative group">
                <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 p-1 shadow-2xl">
                  <img
                    src={
                      user.avatarUrl ||
                      "https://media.istockphoto.com/id/1925148297/fr/photo/sci-fi-landscape-with-glowing-neon-sphere.webp?a=1&b=1&s=612x612&w=0&k=20&c=NLt8FX7bc5JzWmbVXXL8pvpKmAeDG9uNcefMRydu_7E="
                    }
                    alt="Avatar"
                    className="w-full h-full object-cover rounded-full bg-gray-800"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-gray-900 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 pt-6 pb-6 text-center lg:text-left">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      {user.username}
                    </h1>
                    <p className="text-gray-400 text-lg">@{user.username}</p>
                  </div>

                  {user.bio && (
                    <p className="text-gray-300 text-base max-w-2xl leading-relaxed">
                      {user.bio}
                    </p>
                  )}

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 text-sm text-gray-400">
                    <div className="flex items-center space-x-1.5">
                      <Calendar size={16} />
                      <span>
                        Joined{" "}
                        {user.createdAt
                          ? new Date(user.createdAt as any).toLocaleDateString(
                              "en-US",
                              {
                                month: "long",
                                year: "numeric",
                              }
                            )
                          : "N/A"}
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-1.5">
                      <Users size={16} />
                      <span>1.2K followers</span>
                    </div>

                    <div className="flex items-center space-x-1.5">
                      <Heart size={16} />
                      <span>3.5K likes</span>
                    </div>
                  </div>

                  {/* Social Links */}
                  {(user.socials?.twitter ||
                    user.socials?.facebook ||
                    user.socials?.instagram ||
                    user.socials?.tiktok ||
                    user.socials?.snap) && (
                    <div className="flex items-center justify-center lg:justify-start space-x-4 pt-2">
                      {user.socials?.twitter && (
                        <a
                          href={user.socials.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-400 transition-colors duration-200 hover:scale-110 transform"
                        >
                          <Twitter size={20} />
                        </a>
                      )}
                      {user.socials?.facebook && (
                        <a
                          href={user.socials.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600 transition-colors duration-200 hover:scale-110 transform"
                        >
                          <Facebook size={20} />
                        </a>
                      )}
                      {user.socials?.instagram && (
                        <a
                          href={user.socials.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-pink-500 transition-colors duration-200 hover:scale-110 transform"
                        >
                          <Instagram size={20} />
                        </a>
                      )}
                      {user.socials?.tiktok && (
                        <a
                          href={user.socials.tiktok}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-white transition-colors duration-200 hover:scale-110 transform"
                        >
                          <Link2 size={20} />
                        </a>
                      )}
                      {user.socials?.snap && (
                        <a
                          href={user.socials.snap}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 hover:scale-110 transform"
                        >
                          <Link2 size={20} />
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons - Only for non-owners */}
                {!isOwner && (
                  <div className="flex flex-col sm:flex-row gap-3 mt-6 lg:mt-0 lg:ml-6">
                    <button
                      onClick={handleFollow}
                      className={`${
                        isFollowing
                          ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                          : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      } text-white font-semibold py-3 px-6 rounded-full flex items-center justify-center space-x-2 transition-all duration-200 transform hover:scale-105 shadow-lg min-w-[120px]`}
                    >
                      <Rss size={16} />
                      <span>{isFollowing ? "Following" : "Follow"}</span>
                    </button>

                    <button className="bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-full flex items-center justify-center space-x-2 transition-all duration-200 transform hover:scale-105 border border-gray-600 shadow-lg min-w-[120px]">
                      <MessageSquare size={16} />
                      <span>Message</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
